/**
 * SquaresModal — iter87.582 (core redesign). Player-facing Squares
 * surface: status + self opt-in/out, the "Luck of the Draw" explainer
 * (auto mode), or the manual-mode draft/save/claim flow:
 *   TBD ("none") -> choose an allotment (implicit opt-in) -> tap open
 *   boxes into a LOCAL draft -> Save (batch-persists, first-to-save
 *   wins any collision) -> once saved === allotment, Claim Squares
 *   freezes the grid -> Unclaim reopens it without releasing boxes.
 * Clear All wipes both the unsaved draft and any already-saved boxes
 * (blocked once claimed — Unclaim first).
 */
import { useCallback, useEffect, useState } from "react";
import { X, Grid3x3, Lock, LogOut, LogIn, PartyPopper, Sparkles, Trash2, Unlock } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useBrutalConfirm } from "@/components/BrutalConfirm";

function initialsOf(name) {
    const n = (name || "").trim();
    if (!n) return "—";
    // iter87.582.1 (testing-agent fix) — plain first-2-chars collided
    // for fixture names sharing a prefix (e.g. "Redesign_P2" vs
    // "Redesign_P3" both rendered "RE"); prefer the LAST word's first
    // 2 chars when a name has more than one word/segment.
    const parts = n.split(/[^a-zA-Z0-9]+/).filter(Boolean);
    const seg = parts.length > 1 ? parts[parts.length - 1] : parts[0];
    return (seg || n).slice(0, 2).toUpperCase() || "—";
}

// iter87.553 — Cheer Section results grid highlight. `wins` is the list
// of checkpoint keys (q1/q2/q3/final) a box won; Final gets a gold glow,
// any quarter win gets a cyan ring. A box can carry both.
function winnerRingCls(wins) {
    if (!wins || wins.length === 0) return "";
    if (wins.includes("final")) return "border-2 border-amber-300 shadow-[0_0_8px_-1px_rgba(252,211,77,0.85)]";
    return "border-2 border-cyan-400";
}

function SquaresGridRow({ r, byIdx, digits, meId, onToggle, locked, winnersByIdx, highlightMine = true, testIdPrefix, draftSet }) {
    return (
        <>
            <div className="text-center text-[10px] font-bold text-heat flex items-center justify-center">
                {digits ? digits.away[r] : "?"}
            </div>
            {Array.from({ length: 10 }).map((_, c) => {
                const idx = r * 10 + c;
                const box = byIdx[idx] || { box_index: idx, participant_id: null, username: null };
                const mineSaved = !!meId && box.participant_id === meId;
                const takenByOther = !!box.participant_id && !mineSaved;
                const isDraft = !!draftSet?.has(idx);
                const wins = winnersByIdx?.[idx];
                const winCls = winnerRingCls(wins);
                let cellCls, label, boxState;
                if (!highlightMine) {
                    // Cheer Section (read-only, post-finalize) — ownership is
                    // a background tint only; the border is reserved for the
                    // win ring so the two never look identical at a glance.
                    const taken = !!box.participant_id;
                    cellCls = taken
                        ? `bg-zinc-800 text-zinc-400 ${mineSaved ? "ring-1 ring-inset ring-volt/50 bg-volt/10" : "border-zinc-700"}`
                        : "border-zinc-700 bg-zinc-900 text-zinc-600";
                    label = taken ? initialsOf(box.username) : "";
                    boxState = taken ? "taken" : "open";
                } else if (mineSaved) {
                    cellCls = "border-2 border-volt bg-volt/20 text-volt";
                    label = initialsOf(box.username);
                    boxState = "mine-saved";
                } else if (isDraft) {
                    cellCls = "border-2 border-dashed border-amber-400 bg-amber-400/15 text-amber-300";
                    label = "•";
                    boxState = "draft";
                } else if (takenByOther) {
                    cellCls = "bg-zinc-800 text-zinc-400 border-zinc-700";
                    label = initialsOf(box.username);
                    boxState = "taken";
                } else {
                    cellCls = "border-zinc-700 bg-zinc-900 text-zinc-600 hover:border-volt/50 hover:text-volt";
                    label = "";
                    boxState = "open";
                }
                const disabled = locked || (highlightMine && (takenByOther || mineSaved));
                return (
                    <button
                        key={idx}
                        type="button"
                        disabled={disabled}
                        onClick={() => onToggle(box)}
                        data-testid={`${testIdPrefix}-box-${idx}`}
                        data-box-state={boxState}
                        title={box.username || (isDraft ? "Pending save" : "Open")}
                        className={`w-[27px] h-[27px] rounded-sm border text-[8px] font-bold flex items-center justify-center transition ${cellCls} ${winCls}`}
                    >
                        {label}
                    </button>
                );
            })}
        </>
    );
}

function SquaresGrid({ digits, boxes, meId, onToggle, locked, winnersByIdx, highlightMine = true, testIdPrefix = "squares", draftSet }) {
    const byIdx = {};
    boxes.forEach((b) => { byIdx[b.box_index] = b; });
    return (
        <div className="overflow-x-auto" data-testid={`${testIdPrefix}-grid`}>
            <div className="inline-grid gap-[3px]" style={{ gridTemplateColumns: "20px repeat(10, 27px)" }}>
                <div />
                {Array.from({ length: 10 }).map((_, c) => (
                    <div key={`h-${c}`} className="text-center text-[10px] font-bold text-cyber">
                        {digits ? digits.home[c] : "?"}
                    </div>
                ))}
                {Array.from({ length: 10 }).map((_, r) => (
                    <SquaresGridRow key={r} r={r} byIdx={byIdx} digits={digits} meId={meId} onToggle={onToggle} locked={locked} winnersByIdx={winnersByIdx} highlightMine={highlightMine} testIdPrefix={testIdPrefix} draftSet={draftSet} />
                ))}
            </div>
        </div>
    );
}

const STATUS_LABEL = {
    confirmed: "Confirmed", opted_in: "Opted In",
    dropped: "Not Playing", none: "TBD — pick your squares",
};

export default function SquaresModal({ open, onOpenChange, tournament, me, squaresState, onRefresh }) {
    const tid = tournament?.id;
    const meId = me?.id;
    const [board, setBoard] = useState(null);
    const [checkpointsData, setCheckpointsData] = useState(null);
    const [roster, setRoster] = useState([]);
    const [draftBoxes, setDraftBoxes] = useState([]);
    const [busyOpt, setBusyOpt] = useState(false);
    const [busyAllotment, setBusyAllotment] = useState(false);
    const [busySave, setBusySave] = useState(false);
    const [busyClaim, setBusyClaim] = useState(false);
    const [busyUnclaim, setBusyUnclaim] = useState(false);
    const [busyClear, setBusyClear] = useState(false);
    const [busyAuto, setBusyAuto] = useState(false);
    const { confirm: brutalConfirm, hostElement: confirmHost } = useBrutalConfirm();

    const loadBoard = useCallback(async () => {
        if (!tid || !open) return;
        try {
            const data = await api.squaresGetBoard(tid);
            setBoard(data);
        } catch (e) { /* best-effort */ }
    }, [tid, open]);

    // iter87.551 — live, display-only checkpoint results (never moves
    // tokens itself — Finalize is an admin-tab-only action).
    const loadCheckpoints = useCallback(async () => {
        if (!tid || !open) return;
        try {
            const data = await api.squaresGetCheckpoints(tid);
            setCheckpointsData(data);
        } catch (e) { /* best-effort */ }
    }, [tid, open]);

    useEffect(() => {
        if (open) { loadBoard(); loadCheckpoints(); onRefresh?.(); }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, loadBoard, loadCheckpoints]);

    // iter87.582 — the local draft is per-modal-session only; clear it
    // whenever the modal opens (fresh start) or closes (don't leak a
    // stale draft into the next open).
    useEffect(() => { setDraftBoxes([]); }, [open, tid]);

    // iter87.553.1 (testing-agent fix) — the Cheer Section leaderboard
    // needs every CONFIRMED player, including anyone who claimed boxes
    // but won 0 tokens (the ledger only has rows for actual winners),
    // so load the roster once the board is finalized.
    useEffect(() => {
        if (!tid || !open || !checkpointsData?.finalized) return;
        api.squaresGetRoster(tid).then((d) => setRoster(d.roster || [])).catch(() => {});
    }, [tid, open, checkpointsData?.finalized]);

    // iter87.551 fix (testing-agent caught) — Live Results never
    // refreshed while the modal stayed open during a live game; poll
    // every 45s while it's open, locked, and the game isn't final yet.
    useEffect(() => {
        if (!open || !checkpointsData?.locked || checkpointsData?.game_finished) return undefined;
        const id = setInterval(loadCheckpoints, 45000);
        return () => clearInterval(id);
    }, [open, checkpointsData?.locked, checkpointsData?.game_finished, loadCheckpoints]);

    if (!open || !tournament || !me) return null;
    const st = squaresState || {};

    // iter87.550 fix (testing-agent caught) — the tile/hook already
    // reads pro_plus_ok off this same config payload; the modal must
    // honor it too instead of rendering a full pick UI that 402s on
    // every action for a non-Pro+ league.
    if (st.pro_plus_ok === false) {
        return (
            <div
                className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80"
                data-testid="squares-modal-pro-gate-backdrop"
                onClick={(e) => { if (e.target === e.currentTarget) onOpenChange?.(false); }}
            >
                <div className="brutal-card max-w-md w-full p-5" data-testid="squares-modal-pro-gate" onClick={(e) => e.stopPropagation()}>
                    <div className="flex items-center justify-between gap-3 mb-3">
                        <h2 className="font-display font-black text-lg uppercase text-white">Squares</h2>
                        <button onClick={() => onOpenChange?.(false)} className="brutal-btn-ghost p-1.5" data-testid="squares-modal-pro-gate-close-btn" aria-label="Close Squares">
                            <X className="w-4 h-4" />
                        </button>
                    </div>
                    <p className="text-sm text-zinc-400">Squares is a Pro+ subscriber feature. Ask your rivalry admin to upgrade to unlock it.</p>
                </div>
            </div>
        );
    }

    const mode = board?.mode || st.mode || "manual";
    const sport = (board?.sport || st.sport || "nfl").toUpperCase();
    const game = board?.game || st.game;
    const cfg = board?.config || st.config || {};
    const locked = board?.locked ?? st.locked ?? false;
    const myState = st.my_state || "none";
    const myClaimed = !!st.my_claimed;
    const myAllotment = st.my_allotment || 0;
    const boxes = board?.boxes || Array.from({ length: 100 }, (_, i) => ({ box_index: i, participant_id: null, username: null }));
    const savedCount = boxes.filter((b) => b.participant_id === meId).length;
    const room = Math.max(0, myAllotment - savedCount);
    const readyToClaim = !myClaimed && myAllotment > 0 && savedCount >= myAllotment && draftBoxes.length === 0;
    // iter87.582.1 (testing-agent fix) — an "opted_in"/"confirmed"
    // player who hasn't actually chosen an allotment yet (e.g. right
    // after opting back in post-optout) must be treated like "none":
    // no footer, no picking, just the allotment prompt above the grid.
    const hasAllotment = myAllotment > 0;
    const canPick = mode === "manual" && !locked && !myClaimed && myState !== "dropped" && hasAllotment;
    const costCopy = cfg.allocation_basis === "per_player" ? `${cfg.amount} tokens flat` : `${cfg.amount} tokens / box`;
    const committedTotal = cfg.allocation_basis === "per_player" ? (myAllotment > 0 ? cfg.amount : 0) : (cfg.amount || 0) * savedCount;
    // iter87.583 (user-requested) — total the player is on the hook
    // for based on their chosen ALLOTMENT (what they're committing to
    // buy overall), not just what's saved so far — e.g. "10 boxes x
    // 10 tokens = 100 tokens", shown as soon as an allotment exists.
    const totalOwed = cfg.allocation_basis === "per_player" ? cfg.amount : (cfg.amount || 0) * myAllotment;
    const myBoxCount = savedCount; // auto-mode (post-lottery) label reuse

    const minAllot = cfg.min_squares_per_player || 1;
    const maxAllot = cfg.max_squares_per_player || 20;
    const allotmentOptions = Array.from({ length: Math.max(0, maxAllot - minAllot + 1) }, (_, i) => minAllot + i);

    // iter87.553 — Cheer Section: winning boxes per checkpoint (for the
    // results grid highlight) + a token leaderboard, both derived from
    // the same `checkpointsData` the Live Results card already polls.
    const winnersByIdx = {};
    if (checkpointsData?.finalized) {
        Object.entries(checkpointsData.checkpoints || {}).forEach(([cp, r]) => {
            if (r.ready && r.box_index != null) {
                (winnersByIdx[r.box_index] = winnersByIdx[r.box_index] || []).push(cp);
            }
        });
    }
    const leaderboard = (() => {
        if (!checkpointsData?.finalized) return [];
        const usernameById = {};
        boxes.forEach((b) => { if (b.participant_id) usernameById[b.participant_id] = b.username; });
        roster.forEach((r) => { usernameById[r.id] = r.username; });
        const totals = {};
        roster.filter((r) => r.state === "confirmed").forEach((r) => { totals[r.id] = 0; });
        (checkpointsData.ledger || []).forEach((a) => {
            totals[a.participant_id] = (totals[a.participant_id] || 0) + a.tokens_awarded;
        });
        const rows = Object.entries(totals)
            .map(([pid, tokens]) => ({ participant_id: pid, username: usernameById[pid] || pid.slice(0, 8), tokens }))
            .sort((a, b) => b.tokens - a.tokens);
        // iter87.553.1 (testing-agent fix) — tied token totals share the
        // same rank number instead of incrementing sequentially.
        let rank = 0;
        let prevTokens = null;
        return rows.map((row, i) => {
            if (row.tokens !== prevTokens) { rank = i + 1; prevTokens = row.tokens; }
            return { ...row, rank };
        });
    })();

    const doSelfOptOut = async () => {
        setBusyOpt(true);
        try {
            await api.squaresSelfOptOut(tid, meId);
            toast.success("You're opted out of Squares");
            setDraftBoxes([]);
            onRefresh?.(); await loadBoard();
        } catch (err) { toast.error(err.response?.data?.detail || "Couldn't opt out"); }
        setBusyOpt(false);
    };
    const doSelfOptIn = async () => {
        setBusyOpt(true);
        try {
            await api.squaresSelfOptIn(tid, meId);
            toast.success("You're opted back in");
            onRefresh?.(); await loadBoard();
        } catch (err) { toast.error(err.response?.data?.detail || "Couldn't opt in"); }
        setBusyOpt(false);
    };

    const doSetAllotment = async (n) => {
        if (!n) return;
        setBusyAllotment(true);
        try {
            const resp = await api.squaresSetAllotment(tid, meId, n);
            if (resp.adjusted) toast(`Clamped to ${resp.allotment} boxes — that's the most still achievable`);
            else toast.success(`Allotment set to ${resp.allotment} boxes`);
            onRefresh?.(); await loadBoard();
        } catch (err) { toast.error(err.response?.data?.detail || "Couldn't set allotment"); }
        setBusyAllotment(false);
    };

    const toggleDraftBox = (box) => {
        if (!canPick) {
            if (myState === "dropped") toast.error("Opt back in first");
            else if (!hasAllotment) toast.error("Choose how many boxes you want first");
            else if (myClaimed) toast.error("Unclaim first to change your picks");
            return;
        }
        if (box.participant_id) return; // already saved (mine) or taken — no single-box release anymore
        const idx = box.box_index;
        if (draftBoxes.includes(idx)) {
            setDraftBoxes(draftBoxes.filter((i) => i !== idx));
            return;
        }
        if (draftBoxes.length >= room) {
            toast.error(`You've already picked all ${myAllotment} boxes`);
            return;
        }
        setDraftBoxes([...draftBoxes, idx]);
    };

    const doAutoSelect = async () => {
        setBusyAuto(true);
        try {
            const resp = await api.squaresAutoSelect(tid, meId);
            // iter87.582.1 (testing-agent fix) — the server can't see
            // our local draft, so its suggestions are sized to
            // (allotment - saved), ignoring whatever's already
            // drafted. Cap the merge to the room actually left for
            // NEW picks, or Save would silently drop the overflow.
            const roomForNew = Math.max(0, room - draftBoxes.length);
            const additions = (resp.box_indexes || []).filter((i) => !draftBoxes.includes(i)).slice(0, roomForNew);
            if (additions.length > 0) {
                setDraftBoxes([...draftBoxes, ...additions]);
            } else {
                toast("No room left to auto-fill");
            }
        } catch (err) { toast.error(err.response?.data?.detail || "Auto-fill failed"); }
        setBusyAuto(false);
    };

    const doSave = async () => {
        if (draftBoxes.length === 0) return;
        setBusySave(true);
        try {
            const resp = await api.squaresSavePicks(tid, meId, draftBoxes);
            setDraftBoxes([]);
            if (resp.rejected?.length) {
                toast.error(`${resp.rejected.length} box${resp.rejected.length === 1 ? "" : "es"} got taken first — saved the rest`);
            } else {
                toast.success(`Saved ${resp.saved.length} box${resp.saved.length === 1 ? "" : "es"}`);
            }
            if (resp.adjusted) toast(`Your allotment was clamped to ${resp.allotment} — the board filled up around you`);
            onRefresh?.(); await loadBoard();
        } catch (err) {
            toast.error(err.response?.data?.detail || "Save failed");
            await loadBoard();
        }
        setBusySave(false);
    };

    const doClaim = async () => {
        setBusyClaim(true);
        try {
            await api.squaresClaimPicks(tid, meId);
            toast.success("Squares claimed — you're locked in!");
            onRefresh?.(); await loadBoard();
        } catch (err) { toast.error(err.response?.data?.detail || "Couldn't claim"); }
        setBusyClaim(false);
    };

    const doUnclaim = async () => {
        setBusyUnclaim(true);
        try {
            await api.squaresUnclaim(tid, meId);
            toast.success("Unclaimed — you can edit your picks again");
            onRefresh?.(); await loadBoard();
        } catch (err) { toast.error(err.response?.data?.detail || "Couldn't unclaim"); }
        setBusyUnclaim(false);
    };

    const doClearAll = async () => {
        const ok = await brutalConfirm({
            title: "Clear all your squares?",
            body: "This releases every box you've saved so far and clears any unsaved picks. You'll start over from zero.",
            confirmLabel: "Clear all",
            destructive: true,
        });
        if (!ok) return;
        setBusyClear(true);
        try {
            await api.squaresClearAll(tid, meId);
            setDraftBoxes([]);
            toast.success("Cleared — pick again whenever you're ready");
            onRefresh?.(); await loadBoard();
        } catch (err) { toast.error(err.response?.data?.detail || "Couldn't clear"); }
        setBusyClear(false);
    };

    return (
        <div
            className="fixed inset-0 z-50 flex items-start lg:items-center justify-center p-0 lg:p-4 bg-black/80"
            data-testid="squares-modal-backdrop"
            onClick={(e) => { if (e.target === e.currentTarget) onOpenChange?.(false); }}
        >
            {confirmHost}
            <div className="brutal-card w-full lg:max-w-2xl h-[100dvh] lg:h-auto lg:max-h-[88dvh] flex flex-col" data-testid="squares-modal" onClick={(e) => e.stopPropagation()}>
                <div className="flex items-center justify-between gap-3 p-4 border-b-2 border-[#222] shrink-0">
                    <div className="flex items-center gap-2 min-w-0">
                        <Grid3x3 className="w-5 h-5 text-volt shrink-0" />
                        <h2 className="font-display font-black text-lg uppercase truncate text-white">
                            {sport} {mode === "auto" ? "Luck of the Draw" : "Squares"}
                        </h2>
                        {locked && <Lock className="w-3.5 h-3.5 text-blaze shrink-0" />}
                    </div>
                    <button onClick={() => onOpenChange?.(false)} className="brutal-btn-ghost p-1.5" data-testid="squares-modal-close-btn" aria-label="Close Squares">
                        <X className="w-4 h-4" />
                    </button>
                </div>

                <div className="flex-1 overflow-y-auto p-4 space-y-4">
                    {game && (
                        <div className="rounded-lg border border-white/10 bg-ink p-3 text-sm" data-testid="squares-modal-game">
                            <div className="font-bold text-white">{game.away_name} @ {game.home_name}</div>
                            <div className="text-[11px] text-zinc-500 mt-0.5">
                                {game.scheduled_at ? new Date(game.scheduled_at).toLocaleString() : "Kickoff TBD"}
                                {locked ? " · Locked" : " · Picks open until kickoff"}
                            </div>
                        </div>
                    )}

                    <div className="rounded-lg border border-white/10 bg-ink p-3 flex items-center justify-between gap-3" data-testid="squares-modal-status">
                        <div className="text-sm min-w-0">
                            <div className="text-[10px] uppercase tracking-wide text-zinc-500">Your status</div>
                            <div
                                className={`font-bold ${myState === "confirmed" || myClaimed ? "text-volt" : myState === "dropped" ? "text-zinc-500" : "text-heat"}`}
                                data-testid="squares-my-status"
                            >
                                {myClaimed ? "Claimed" : (STATUS_LABEL[myState] || myState)}
                            </div>
                            <div className="text-[11px] text-zinc-500 mt-0.5">Buy-in: {costCopy}</div>
                            {hasAllotment && mode === "manual" && (
                                <div className="text-[11px] text-heat font-bold mt-0.5" data-testid="squares-total-buyin">
                                    {cfg.allocation_basis === "per_player"
                                        ? `Total buy-in: ${totalOwed} tokens flat`
                                        : `Total buy-in: ${myAllotment} box${myAllotment === 1 ? "" : "es"} × ${cfg.amount} = ${totalOwed} tokens`}
                                </div>
                            )}
                        </div>
                        {!locked && myState !== "confirmed" && !myClaimed && (
                            myState === "dropped" ? (
                                <button onClick={doSelfOptIn} disabled={busyOpt} data-testid="squares-self-optin-btn"
                                    className="brutal-btn-ghost text-xs font-bold uppercase px-3 py-2 flex items-center gap-1.5 shrink-0">
                                    <LogIn className="w-3.5 h-3.5" /> Opt In
                                </button>
                            ) : (
                                <button onClick={doSelfOptOut} disabled={busyOpt} data-testid="squares-self-optout-btn"
                                    className="brutal-btn-ghost text-xs font-bold uppercase px-3 py-2 flex items-center gap-1.5 shrink-0 border-blaze/40 text-blaze">
                                    <LogOut className="w-3.5 h-3.5" /> Not Interested
                                </button>
                            )
                        )}
                    </div>

                    {mode === "auto" && (
                        <div className="rounded-lg border border-cyber/40 bg-cyber/[0.06] p-3 text-[12px] text-zinc-300 leading-relaxed" data-testid="squares-luck-card">
                            <p className="font-bold text-cyber mb-1 uppercase text-[11px] tracking-wide">How this works</p>
                            <p>
                                Boxes are auto-assigned — no picking needed. {locked ? "The board has locked and" : "The moment the board locks (kickoff),"} all 100
                                boxes are randomly and evenly split among every Confirmed crewmate. Payout is{" "}
                                {cfg.payout_type === "wta" ? "Winner Take All on the final score." : "split across Q1/Q2/Q3/Final checkpoints."}
                            </p>
                            {locked && myBoxCount > 0 && (
                                <p className="mt-2 text-white font-bold" data-testid="squares-my-box-count">
                                    You were dealt {myBoxCount} box{myBoxCount === 1 ? "" : "es"} — see them highlighted below.
                                </p>
                            )}
                        </div>
                    )}

                    {mode === "manual" && !locked && myState !== "dropped" && (
                        <div className="rounded-lg border border-white/10 bg-ink p-3 flex items-center justify-between gap-3 flex-wrap" data-testid="squares-allotment-card">
                            <div>
                                <div className="text-[10px] uppercase tracking-wide text-zinc-500">How many boxes?</div>
                                <div className="text-[11px] text-zinc-500 mt-0.5">
                                    Pick {minAllot}–{maxAllot} boxes · {st.remaining_open ?? 100} open on the board
                                </div>
                            </div>
                            <select
                                value={myAllotment || ""}
                                disabled={busyAllotment || myClaimed}
                                onChange={(e) => doSetAllotment(parseInt(e.target.value, 10))}
                                data-testid="squares-allotment-select"
                                className="rounded-lg bg-[#0e0e0e] border border-white/[0.10] focus:border-volt/60 focus:outline-none px-3 py-2 text-sm text-white font-bold disabled:opacity-60"
                            >
                                <option value="" disabled>Choose…</option>
                                {allotmentOptions.map((n) => (<option key={n} value={n}>{n} boxes</option>))}
                            </select>
                        </div>
                    )}

                    {(mode === "manual" || (mode === "auto" && locked)) && (
                        <div className="rounded-lg border border-white/10 bg-ink p-3" data-testid="squares-grid-card">
                            <div className="flex items-start justify-between gap-2 mb-2">
                                <p className="text-[11px] text-zinc-500">
                                    {locked
                                        ? "Board locked — box owners are shown on the grid."
                                        : myClaimed
                                            ? "Your squares are claimed and locked — Unclaim below to edit them."
                                            : "Tap an open box to add it to your draft, then hit Save to lock it in."}
                                </p>
                                {canPick && (
                                    <button type="button" onClick={doAutoSelect} disabled={busyAuto || room === 0}
                                        data-testid="squares-auto-select-btn"
                                        className="brutal-btn-ghost px-2.5 py-1 text-[10px] font-bold uppercase shrink-0 flex items-center gap-1 disabled:opacity-40">
                                        <Sparkles className="w-3 h-3" /> {busyAuto ? "…" : "Auto-fill"}
                                    </button>
                                )}
                            </div>
                            <div className="flex items-center justify-center gap-4 text-[10px] font-bold uppercase mb-1.5">
                                <span className="text-cyber">Columns: {game?.home_name || "Home"}</span>
                                <span className="text-heat">Rows: {game?.away_name || "Away"}</span>
                            </div>
                            <div className="flex justify-center">
                                <SquaresGrid digits={board?.digits} boxes={boxes} meId={meId} onToggle={toggleDraftBox} locked={locked || mode === "auto"} draftSet={new Set(draftBoxes)} />
                            </div>
                            {mode === "manual" && !locked && (
                                <div className="flex items-center justify-center gap-3 text-[9px] uppercase tracking-wide text-zinc-500 mt-2">
                                    <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-sm border-2 border-volt" /> Saved</span>
                                    <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-sm border-2 border-dashed border-amber-400" /> Unsaved pick</span>
                                    <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-sm bg-zinc-800 border border-zinc-700" /> Taken</span>
                                </div>
                            )}
                        </div>
                    )}

                    {locked && checkpointsData && (
                        <div className="rounded-lg border border-white/10 bg-ink p-3" data-testid="squares-checkpoints-card">
                            <div className="text-[10px] uppercase tracking-wide text-zinc-500 mb-2">Live Results</div>
                            <div className="space-y-1.5">
                                {Object.entries(checkpointsData.checkpoints || {}).map(([cp, r]) => (
                                    <div key={cp} className="flex items-start justify-between gap-2 text-sm" data-testid={`squares-checkpoint-${cp}`}>
                                        <span className="uppercase text-[11px] font-bold text-zinc-400 shrink-0 pt-0.5">{cp === "final" ? "Final" : cp.toUpperCase()}</span>
                                        {!r.ready ? (
                                            <span className="text-zinc-400 text-xs">Pending</span>
                                        ) : (
                                            <span className={`text-xs font-bold text-right ${r.participant_id === meId ? "text-volt" : "text-zinc-300"}`}>
                                                {game?.away_name || "Away"} {r.away_score} – {game?.home_name || "Home"} {r.home_score} → {r.username ? `${r.username}${r.participant_id === meId ? " (You!)" : ""}` : "unclaimed box"}
                                            </span>
                                        )}
                                    </div>
                                ))}
                            </div>
                            {checkpointsData.finalized && (myState === "confirmed" || myClaimed) && (
                                <div className="mt-2 pt-2 border-t border-white/10 text-sm font-bold text-volt" data-testid="squares-my-total-award">
                                    You won {(checkpointsData.ledger || []).filter((a) => a.participant_id === meId).reduce((s, a) => s + a.tokens_awarded, 0)} tokens total
                                </div>
                            )}
                        </div>
                    )}

                    {checkpointsData?.finalized && (
                        <div className="rounded-lg border border-volt/30 bg-volt/[0.04] p-3 space-y-3" data-testid="squares-cheer-card">
                            <div className="text-[10px] uppercase tracking-wide text-volt font-bold flex items-center gap-1.5">
                                <PartyPopper className="w-3.5 h-3.5" /> Cheer Section
                            </div>
                            {checkpointsData.cheer?.text ? (
                                <p className="text-[13px] text-zinc-200 leading-relaxed italic" data-testid="squares-cheer-text">
                                    “{checkpointsData.cheer.text}”
                                </p>
                            ) : (
                                <p className="text-[11px] text-zinc-500" data-testid="squares-cheer-pending">
                                    The roast recap is still being written up — check back shortly.
                                </p>
                            )}

                            {leaderboard.length > 0 && (
                                <div>
                                    <div className="text-[10px] uppercase tracking-wide text-zinc-500 mb-1.5">Leaderboard</div>
                                    <div className="space-y-1">
                                        {leaderboard.map((row, i) => (
                                            <div
                                                key={row.participant_id}
                                                data-testid={`squares-leaderboard-row-${i}`}
                                                className={`flex items-center justify-between text-xs rounded px-2 py-1 ${row.participant_id === meId ? "bg-volt/10 text-volt font-bold" : "text-zinc-300"}`}
                                            >
                                                <span>#{row.rank} {row.username}{row.participant_id === meId ? " (You!)" : ""}</span>
                                                <span>{row.tokens} tokens</span>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}

                            <div>
                                <div className="flex items-center justify-center gap-3 text-[9px] uppercase tracking-wide text-zinc-500 mb-1.5">
                                    <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-sm border-2 border-amber-300" /> Final winner</span>
                                    <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-sm border-2 border-cyan-400" /> Quarter winner</span>
                                    <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-sm ring-1 ring-inset ring-volt/50 bg-volt/10" /> Your box</span>
                                </div>
                                <div className="flex justify-center">
                                    <SquaresGrid digits={board?.digits} boxes={boxes} meId={meId} onToggle={() => {}} locked winnersByIdx={winnersByIdx} highlightMine={false} testIdPrefix="squares-cheer" />
                                </div>
                            </div>
                        </div>
                    )}
                </div>

                {mode === "manual" && !locked && myState !== "dropped" && hasAllotment && (
                    <div className="shrink-0 border-t-2 border-[#222] p-3 space-y-2" data-testid="squares-modal-footer">
                        {myClaimed ? (
                            <div className="flex items-center justify-between gap-3">
                                <div className="text-sm font-bold text-volt flex items-center gap-1.5" data-testid="squares-claimed-badge">
                                    <PartyPopper className="w-4 h-4" /> {savedCount} box{savedCount === 1 ? "" : "es"} claimed — you're locked in!
                                </div>
                                <button type="button" onClick={doUnclaim} disabled={busyUnclaim} data-testid="squares-unclaim-btn"
                                    className="brutal-btn-ghost px-3 py-2 text-xs font-bold uppercase border-blaze/40 text-blaze flex items-center gap-1.5 shrink-0 disabled:opacity-50">
                                    <Unlock className="w-3.5 h-3.5" /> {busyUnclaim ? "…" : "Unclaim"}
                                </button>
                            </div>
                        ) : (
                            <>
                                <div className="flex items-center justify-between gap-3">
                                    <div className="text-sm font-bold" data-testid="squares-footer-counter">
                                        <span className="text-white">{savedCount + draftBoxes.length}</span>
                                        <span className="text-zinc-500">/</span>
                                        <span className="text-volt">{myAllotment || "—"}</span>
                                        <span className="text-zinc-500 ml-1.5 uppercase text-[10px] tracking-wide">
                                            boxes {draftBoxes.length > 0 ? "(unsaved)" : "picked"}
                                        </span>
                                    </div>
                                    <div className="text-sm font-bold text-volt" data-testid="squares-footer-total">
                                        {committedTotal} tokens committed
                                    </div>
                                </div>
                                <div className="flex items-center gap-2">
                                    <button type="button" onClick={doClearAll} disabled={busyClear || (savedCount === 0 && draftBoxes.length === 0)}
                                        data-testid="squares-clear-all-btn"
                                        className="flex-1 brutal-btn-ghost py-2.5 text-xs font-bold uppercase border-blaze/40 text-blaze flex items-center justify-center gap-1.5 disabled:opacity-40">
                                        <Trash2 className="w-3.5 h-3.5" /> {busyClear ? "Clearing…" : "Clear All"}
                                    </button>
                                    {readyToClaim ? (
                                        <button type="button" onClick={doClaim} disabled={busyClaim} data-testid="squares-claim-btn"
                                            className="flex-[2] brutal-btn-primary py-2.5 text-xs font-bold uppercase disabled:opacity-50">
                                            {busyClaim ? "Claiming…" : "Claim Squares"}
                                        </button>
                                    ) : (
                                        <button type="button" onClick={doSave} disabled={busySave || draftBoxes.length === 0} data-testid="squares-save-btn"
                                            className="flex-[2] brutal-btn-primary py-2.5 text-xs font-bold uppercase disabled:opacity-50">
                                            {busySave ? "Saving…" : `Save${draftBoxes.length ? ` (${draftBoxes.length})` : ""}`}
                                        </button>
                                    )}
                                </div>
                            </>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
}
